"use client";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileInvoiceDollar,
  faSearch,
  faFilter,
  faCreditCard,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faInr,
  faBookOpen,
  faGraduationCap,
  faUserTie,
  faBook,
  faTimes,
  faFileExcel,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { baseUrl } from "../../config/baseUrl";
import moment from "moment/moment";

export default function PaymentReport() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [paymentModeFilter, setPaymentModeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPayments, setTotalPayments] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [paymentsPerPage] = useState(15);
  const [reportSummary, setReportSummary] = useState({
    totalAmount: 0,
    totalCommission: 0,
    totalDiscount: 0,
    totalReferralAmount: 0,
    remainingAmount: 0,
    confirmedPayments: 0,
    pendingPayments: 0,
  });

  const userDetails = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (userDetails?._id) {
      fetchPayments();
    }
  }, [
    currentPage,
    categoryFilter,
    paymentModeFilter,
    statusFilter,
    startDate,
    endDate,
  ]);

  const buildApiUrl = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (categoryFilter) params.append("category", categoryFilter);
    if (paymentModeFilter) params.append("paymentMode", paymentModeFilter);
    if (statusFilter) params.append("status", statusFilter);
    if (currentPage) params.append("page", currentPage.toString());

    const queryString = params.toString();
    return `${baseUrl}/owner/reports/booking-report/${userDetails?._id}${queryString ? `?${queryString}` : ""
      }`;
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const apiUrl = buildApiUrl();
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      });
      const data = await response.json();

      if (data.status) {
        // Transform booking data to payment report format
        const transformedPayments = (data.data.bookingsData || []).map(
          (booking) => ({
            _id: booking._id,
            category: "Library", // Since this is library booking report
            referenceId: booking.bookingId,
            paymentMode: booking.paymentMethod,
            amount: booking.amount,
            bookingDate: booking.createdAt,
            settledDate:
              booking.status === "confirmed" ? booking.updatedAt : null,
            referralMethod: "Direct", // Default for now
            referralId: booking.userId?.userCode || "",
            referralAmount: 0, // Calculate if needed
            discount: booking.amount - booking.remainingAmount || 0,
            coupon: "", // Not available in current response
            commissionPaid: booking.amount - booking.remainingAmount,
            status: booking.status,
            userName: booking.userId?.name || "N/A",
            userEmail: booking.userId?.email || "N/A",
            paidToLibrary: booking.remainingAmount,
            slotInfo: `${booking.fromTime} - ${booking.toTime}`,
            remainingDays: booking.remainingDays,
            transactionId: booking.paymentDetails?.transactionId || "",
            paymentGateway: booking.paymentDetails?.paymentGateway || "",
            couponCode: booking.couponCode,
            couponDiscountAmount: booking.couponDiscountAmount,
          })
        );

        // Apply client-side search filter
        let filteredPayments = transformedPayments;
        if (searchTerm) {
          filteredPayments = transformedPayments.filter(
            (payment) =>
              payment.referenceId
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              payment.userName
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              payment.referralId
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              payment.userEmail
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
          );
        }

        setPayments(filteredPayments);
        setTotalPayments(data.data.totalCount || 0);
        setTotalPages(data.data.totalPages || 1);

        // Update summary from API response
        setReportSummary({
          totalAmount: data.data.totalAmount || 0,
          totalCommission: Number.parseFloat(data.data.totalCommission || 0),
          remainingAmount: Number.parseFloat(data.data.remainingAmount || 0),
          totalDiscount: 0, // Calculate from bookings if needed
          totalReferralAmount: 0, // Calculate from bookings if needed
          confirmedPayments: transformedPayments.filter(
            (p) => p.status === "confirmed"
          ).length,
          pendingPayments: transformedPayments.filter(
            (p) => p.status === "pending"
          ).length,
        });
      } else {
        toast.error(data.message || "Failed to fetch payment report");
        setPayments([]);
      }
    } catch (error) {
      console.error("Error fetching payment report:", error);
      toast.error("Failed to fetch payment report");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      library: faBookOpen,
      exams: faGraduationCap,
      mentors: faUserTie,
      ebooks: faBook,
    };
    return icons[category?.toLowerCase()] || faFileInvoiceDollar;
  };

  const getCategoryBadge = (category) => {
    const colors = {
      library: "primary",
      exams: "success",
      mentors: "warning",
      ebooks: "info",
    };
    const color = colors[category?.toLowerCase()] || "secondary";

    return (
      <span className={`badge bg-${color} category-badge`}>
        <FontAwesomeIcon
          icon={getCategoryIcon(category)}
          className="me-1"
          size="xs"
        />
        {category}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { color: "success", icon: faCheckCircle, text: "Confirmed" },
      pending: { color: "warning", icon: faClock, text: "Pending" },
      cancelled: { color: "danger", icon: faTimesCircle, text: "Cancelled" },
      settled: { color: "info", icon: faCheckCircle, text: "Settled" },
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;

    return (
      <span className={`badge bg-${config.color} status-badge`}>
        <FontAwesomeIcon icon={config.icon} className="me-1" size="xs" />
        {config.text}
      </span>
    );
  };

  const getPaymentModeBadge = (mode) => {
    return (
      <span
        className={`badge payment-mode-badge ${mode === "online" ? "bg-primary" : "bg-secondary"
          }`}
      >
        <FontAwesomeIcon icon={faCreditCard} className="me-1" size="xs" />
        {mode === "online" ? "Online" : "Offline"}
      </span>
    );
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setPaymentModeFilter("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  // const handleExportExcel = () => {
  //     // Implementation for Excel export
  //     const csvContent = generateCSVContent()
  //     downloadCSV(csvContent, "payment-report.csv")
  // }

  const handleExportExcel = () => {
    const params = new URLSearchParams();
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    if (categoryFilter) params.append("category", categoryFilter);
    if (paymentModeFilter) params.append("paymentMode", paymentModeFilter);
    if (statusFilter) params.append("status", statusFilter);
    if (currentPage) params.append("page", currentPage.toString());

    const queryString = params.toString();
    const url = `${baseUrl}/owner/reports/booking-report-download/${userDetails?._id
      }${queryString ? `?${queryString}` : ""}`;

    // Trigger file download
    window.open(url, "_blank");
  };
  const generateCSVContent = () => {
    const headers = [
      "SN",
      "Category",
      "Reference ID",
      "User Name",
      "Payment Mode",
      "Amount",
      "Booking Date",
      "Settled Date",
      "Referral Method",
      "Referral ID",
      "Referral Amount",
      "Discount",
      "Coupon",
      "Platform Fee Paid",
      "Paid to Library",
      "Status",
      "Slot Info",
      "Transaction ID",
    ];

    const csvRows = [headers.join(",")];

    payments.forEach((payment, index) => {
      const row = [
        index + 1,
        payment.category,
        payment.referenceId,
        payment.userName,
        payment.paymentMode,
        payment.amount,
        moment(payment.bookingDate).format("YYYY-MM-DD HH:mm"),
        payment.settledDate
          ? moment(payment.settledDate).format("YYYY-MM-DD HH:mm")
          : "Not settled",
        payment.referralMethod,
        payment.referralId,
        payment.referralAmount,
        payment.discount,
        payment.coupon,
        payment.commissionPaid,
        payment.paidToLibrary,
        payment.status,
        payment.slotInfo,

      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    toast.success("Payment report exported successfully!");
  };

  const handleExportPDF = () => { };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (categoryFilter) count++;
    if (paymentModeFilter) count++;
    if (statusFilter) count++;
    if (startDate) count++;
    if (endDate) count++;
    return count;
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const slider = scrollContainerRef.current;
    let isDown = false;
    let startX;
    let scrollLeft;

    const mouseDownHandler = (e) => {
      isDown = true;
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
      slider.classList.add('dragging');
    };

    const mouseLeaveHandler = () => {
      isDown = false;
      slider.classList.remove('dragging');
    };

    const mouseUpHandler = () => {
      isDown = false;
      slider.classList.remove('dragging');
    };

    const mouseMoveHandler = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 1.5; // Speed multiplier
      slider.scrollLeft = scrollLeft - walk;
    };

    slider.addEventListener('mousedown', mouseDownHandler);
    slider.addEventListener('mouseleave', mouseLeaveHandler);
    slider.addEventListener('mouseup', mouseUpHandler);
    slider.addEventListener('mousemove', mouseMoveHandler);

    return () => {
      slider.removeEventListener('mousedown', mouseDownHandler);
      slider.removeEventListener('mouseleave', mouseLeaveHandler);
      slider.removeEventListener('mouseup', mouseUpHandler);
      slider.removeEventListener('mousemove', mouseMoveHandler);
    };
  }, []);
  return (
    <>
      <section className="main-sec">
        <div className="container-fluid">
          {/* Header */}
          <div className="row mb-4">
            <div className="col-lg-8">
              <div className="dashboard-title">
                <h4 className="dash-head mb-2">
                  <FontAwesomeIcon
                    icon={faFileInvoiceDollar}
                    className="me-2"
                  />
                  Payment Report - {userDetails?.libraryName || "Library"}
                </h4>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="export-buttons">
                {/* <button className="btn btn-success btn-sm me-2" onClick={handleExportExcel}>
                                    <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                                    Export Excel
                                </button> */}
                {/* <Link
                  target="_blank"
                  to={`${baseUrl}/owner/reports/booking-report-download/${userDetails?._id}`}
                  className="btn btn-success btn-sm me-2"
                >
                  <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                  Export Excel
                </Link> */}
                <button
                  className="btn btn-success btn-sm me-2"
                  onClick={handleExportExcel}
                >
                  <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                  Export Excel
                </button>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-lg-12">
              <div className="summary-cards-grid">
                <div className="summary-card total-revenue">
                  <div className="summary-icon">
                    <FontAwesomeIcon icon={faInr} />
                  </div>
                  <div className="summary-content">
                    <h6 className="text-white">Total Revenue</h6>
                    <h4 className="text-white">
                      ₹{reportSummary.totalAmount.toLocaleString()}
                    </h4>
                  </div>
                </div>
                <div className="summary-card total-commission">
                  <div className="summary-icon">
                    <FontAwesomeIcon icon={faInr} />
                  </div>
                  <div className="summary-content">
                    <h6 className="text-white">Total Commission</h6>
                    <h4 className="text-white">
                      ₹{reportSummary.totalCommission.toLocaleString()}
                    </h4>
                  </div>
                </div>
                <div className="summary-card remaining-amount">
                  <div className="summary-icon">
                    <FontAwesomeIcon icon={faInr} />
                  </div>
                  <div className="summary-content">
                    <h6 className="text-white">Remaining Amount</h6>
                    <h4 className="text-white">
                      ₹{reportSummary.remainingAmount.toLocaleString()}
                    </h4>
                  </div>
                </div>
                <div className="summary-card payment-status">
                  <div className="summary-icon">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                  <div className="summary-content">
                    <h6 className="text-white">Confirmed / Pending</h6>
                    <h4 className="text-white">
                      {reportSummary.confirmedPayments} /{" "}
                      {reportSummary.pendingPayments}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Card */}
          <div className="card filter-card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faFilter} className="me-2" />
                Filters{" "}
                {getActiveFiltersCount() > 0 && (
                  <span className="badge bg-primary ms-2">
                    {getActiveFiltersCount()}
                  </span>
                )}
              </h6>
              {getActiveFiltersCount() > 0 && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleClearFilters}
                >
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Clear All
                </button>
              )}
            </div>
            <div className="card-body">
              <div className="row g-3">
                {/* <div className="col-md-6 col-lg-3">
                                    <label className="form-label small fw-medium">Search</label>
                                    <div className="position-relative">
                                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                        <input
                                            type="text"
                                            className="form-control form-control-sm ps-4"
                                            placeholder="Reference ID, User name, Email..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value)
                                                setCurrentPage(1)
                                            }}
                                        />
                                    </div>
                                </div> */}
                {/* <div className="col-md-3 col-lg-2">
                                    <label className="form-label small fw-medium">Category</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={categoryFilter}
                                        onChange={(e) => {
                                            setCategoryFilter(e.target.value)
                                            setCurrentPage(1)
                                        }}
                                    >
                                        <option value="">All Categories</option>
                                        <option value="library">Library</option>
                                        <option value="exams">Exams</option>
                                        <option value="mentors">Mentors</option>
                                        <option value="ebooks">Ebooks</option>
                                    </select>
                                </div> */}
                <div className="col-md-3 col-lg-2">
                  <label className="form-label small fw-medium">
                    Payment Mode
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={paymentModeFilter}
                    onChange={(e) => {
                      setPaymentModeFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Modes</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                  </select>
                </div>
                <div className="col-md-3 col-lg-2">
                  <label className="form-label small fw-medium">Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="pending">Pending</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="col-md-3 col-lg-1.5">
                  <label className="form-label small fw-medium">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <div className="col-md-3 col-lg-1.5">
                  <label className="form-label small fw-medium">End Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {getActiveFiltersCount() > 0 && (
            <div className="active-filters-display mb-3">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <small className="text-muted fw-medium">Active Filters:</small>
                {searchTerm && (
                  <span className="filter-tag">Search: {searchTerm}</span>
                )}
                {categoryFilter && (
                  <span className="filter-tag">Category: {categoryFilter}</span>
                )}
                {paymentModeFilter && (
                  <span className="filter-tag">Mode: {paymentModeFilter}</span>
                )}
                {statusFilter && (
                  <span className="filter-tag">Status: {statusFilter}</span>
                )}
                {startDate && (
                  <span className="filter-tag">
                    From: {moment(startDate).format("MMM DD, YYYY")}
                  </span>
                )}
                {endDate && (
                  <span className="filter-tag">
                    To: {moment(endDate).format("MMM DD, YYYY")}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Results Summary */}
          <div className="results-summary mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                Payment Records
                <span className="text-muted ms-2">
                  ({payments.length} of {totalPayments} results)
                </span>
              </h6>
              <small className="text-muted">
                Page {currentPage} of {totalPages}
              </small>
            </div>
          </div>

          {/* Table Card */}
          <div className="card table-card">
            <div className="table-responsive" ref={scrollContainerRef}>
              <table className="table table-hover align-middle mb-0">
                <thead className="table-header">
                  <tr>
                    <th style={{ width: "50px" }}>SN</th>
                    {/* <th style={{ width: "100px" }}>Category</th> */}
                    <th style={{ width: "140px" }}>Reference ID</th>
                    <th style={{ width: "100px" }}>Payment Mode</th>
                    <th style={{ width: "100px" }}>Amount</th>
                    <th style={{ width: "80px" }}>Platform Fee</th>
                    <th style={{ width: "80px" }}>Coupon Code</th>
                    <th style={{ width: "80px" }}>Discount</th>
                    <th style={{ width: "110px" }}>Booking Date</th>
                    <th style={{ width: "110px" }}>Settled Date</th>
                    <th style={{ width: "100px" }}>Referral</th>
                    {/* <th style={{ width: "80px" }}>Discount</th> */}
                    <th style={{ width: "100px" }}>Slot Info</th>
                    <th style={{ width: "80px" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="12" className="text-center py-5">
                        <div className="loading-container">
                          <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mb-0 text-muted">
                            Loading payment report...
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : payments.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="text-center py-5">
                        <div className="empty-state">
                          <FontAwesomeIcon
                            icon={faFileInvoiceDollar}
                            size="3x"
                            className="text-muted mb-3"
                          />
                          <h5 className="text-muted">
                            No Payment Records Found
                          </h5>
                          <p className="text-muted mb-0">
                            {getActiveFiltersCount() > 0
                              ? "Try adjusting your search or filter criteria"
                              : "No payment records available at the moment"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment, i) => (
                      <tr key={payment._id}>
                        <td className="text-center fw-medium">
                          {(currentPage - 1) * paymentsPerPage + i + 1}
                        </td>
                        {/* <td>{getCategoryBadge(payment.category)}</td> */}
                        <td>
                          <div className="reference-info">
                            <div className="reference-id fw-medium">
                              {payment.referenceId}
                            </div>
                            <div className="user-name text-muted small">
                              {payment.userName}
                            </div>
                            <div className="user-email text-muted small">
                              {payment.userEmail}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="payment-mode-info">
                            {getPaymentModeBadge(payment.paymentMode)}
                            {payment.transactionId && (
                              <div className="transaction-id text-muted small mt-1">
                                TXN: {payment.transactionId}
                              </div>
                            )}
                            {payment.paymentGateway && (
                              <div className="payment-gateway text-muted small">
                                {payment.paymentGateway}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="amount-info">
                            <div className="amount fw-bold text-success">
                              ₹{payment.amount}
                            </div>
                            <div className="paid-amount text-muted small">
                              Paid: ₹{payment.paidToLibrary}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="commission-amount fw-bold text-info">
                            ₹{Number(payment.commissionPaid).toFixed(2)}
                          </div>
                        </td>
                        <td>
                          <div className="commission-amount  ">
                            {payment.couponCode ? payment.couponCode : "Not Used"}
                          </div>
                        </td>
                        <td>
                          <div className="commission-amount fw-bold ">
                            ₹{payment.couponDiscountAmount || 0}
                          </div>
                        </td>
                        <td>
                          <div className="date-info">
                            <div className="date small fw-medium">
                              {moment(payment.bookingDate).format(
                                "MMM DD, YYYY"
                              )}
                            </div>
                            <div className="time text-muted small">
                              {moment(payment.bookingDate).format("HH:mm")}
                            </div>
                          </div>
                        </td>
                        <td>
                          {payment.settledDate ? (
                            <div className="date-info">
                              <div className="date small fw-medium">
                                {moment(payment.settledDate).format(
                                  "MMM DD, YYYY"
                                )}
                              </div>
                              <div className="time text-muted small">
                                {moment(payment.settledDate).format("HH:mm")}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted small">
                              Not settled
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="referral-info">
                            <div className="referral-method small fw-medium">
                              {payment.referralMethod}
                            </div>
                            {payment.referralId && (
                              <div className="referral-id text-muted small">
                                {payment.referralId}
                              </div>
                            )}
                            {payment.referralAmount > 0 && (
                              <div className="referral-amount text-success small">
                                ₹{payment.referralAmount}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="slot-info">
                            <span className="badge bg-light text-dark small">
                              {payment.slotInfo}
                            </span>
                            {payment.remainingDays !== undefined && (
                              <div className="remaining-days text-muted small mt-1">
                                {payment.remainingDays} days left
                              </div>
                            )}
                          </div>
                        </td>
                        <td>{getStatusBadge(payment.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="card-footer">
                <nav aria-label="Payment report pagination">
                  <ul className="pagination pagination-sm justify-content-center mb-0">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 &&
                          pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <li
                            key={pageNumber}
                            className={`page-item ${currentPage === pageNumber ? "active" : ""
                              }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => paginate(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          </li>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <li key={pageNumber} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      return null;
                    })}
                    <li
                      className={`page-item ${currentPage === totalPages ? "disabled" : ""
                        }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Showing {(currentPage - 1) * paymentsPerPage + 1} to{" "}
                    {Math.min(currentPage * paymentsPerPage, totalPayments)} of{" "}
                    {totalPayments} records
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <style>
        {`
        .table-responsive {
  cursor: grab;
}

.table-responsive.dragging {
  cursor: grabbing;
  user-select: none;
}

          .main-sec {
            background-color: #f8f9fa;
            min-height: 100vh;
            padding: 2rem 0;
          }

          .dashboard-title .dash-head {
            color: #2c3e50;
            font-weight: 600;
          }

          .custom-breadcrumb .breadcrumb {
            background: none;
            padding: 0;
          }

          .custom-breadcrumb .breadcrumb-item a {
            color: #6c757d;
            text-decoration: none;
          }

          .custom-breadcrumb .breadcrumb-item.active {
            color: #495057;
          }

          .export-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
          }

          .summary-cards-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }

          .summary-card {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 1.25rem;
            border-radius: 0.5rem;
            display: flex;
            align-items: center;
            gap: 1rem;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            transition: transform 0.2s ease;
          }

          .summary-card:hover {
            transform: translateY(-2px);
          }

          .summary-card.total-revenue {
            background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          }

          .summary-card.total-commission {
            background: linear-gradient(135deg, #007bff 0%, #6610f2 100%);
          }

          .summary-card.remaining-amount {
            background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
          }

          .summary-card.payment-status {
            background: linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%);
          }

          .summary-icon {
            background: rgba(255,255,255,0.2);
            width: 50px;
            height: 50px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.25rem;
          }

          .summary-content h6 {
            margin: 0;
            font-size: 0.875rem;
            opacity: 0.9;
            font-weight: 500;
          }

          .summary-content h4 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 700;
          }

          .filter-card {
            border: none;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .filter-card .card-header {
            background-color: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
          }

          .search-icon {
            position: absolute;
            left: 0.75rem;
            top: 50%;
            transform: translateY(-50%);
            color: #6c757d;
            z-index: 2;
          }

          .active-filters-display {
            background-color: #e3f2fd;
            border: 1px solid #bbdefb;
            border-radius: 0.375rem;
            padding: 0.75rem;
          }

          .filter-tag {
            background-color: #2196f3;
            color: white;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            font-size: 0.75rem;
            font-weight: 500;
          }

          .results-summary {
            background-color: white;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            padding: 0.75rem 1rem;
          }

          .table-card {
            border: none;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .table-header th {
            background-color: #f8f9fa;
            border-bottom: 2px solid #dee2e6;
            font-weight: 600;
            font-size: 0.8rem;
            padding: 0.75rem 0.5rem;
            white-space: nowrap;
          }

          .table td {
            padding: 0.75rem 0.5rem;
            vertical-align: middle;
            border-bottom: 1px solid #f1f3f4;
            font-size: 0.875rem;
          }

          .table tbody tr:hover {
            background-color: #f8f9fa;
          }

          .category-badge,
          .status-badge,
          .payment-mode-badge {
            font-size: 0.7rem;
            padding: 0.25rem 0.5rem;
            font-weight: 500;
          }

          .reference-info .reference-id {
            font-weight: 600;
            color: #495057;
          }

          .reference-info .user-name,
          .reference-info .user-email {
            font-size: 0.75rem;
          }

          .payment-mode-info .transaction-id,
          .payment-mode-info .payment-gateway {
            font-size: 0.7rem;
          }

          .amount-info .amount {
            font-size: 0.9rem;
          }

          .amount-info .paid-amount {
            font-size: 0.7rem;
          }

          .date-info .date {
            font-weight: 500;
          }

          .date-info .time {
            font-size: 0.7rem;
          }

          .referral-info .referral-method {
            font-weight: 500;
            color: #495057;
          }

          .referral-info .referral-id,
          .referral-info .referral-amount {
            font-size: 0.7rem;
          }

          .discount-info .discount-amount {
            font-size: 0.85rem;
          }

          .discount-info .coupon-code {
            font-size: 0.7rem;
            font-style: italic;
          }

          .commission-amount {
            font-size: 0.9rem;
          }

          .slot-info .badge {
            font-size: 0.7rem;
          }

          .slot-info .remaining-days {
            font-size: 0.7rem;
          }

          .loading-container,
          .empty-state {
            padding: 2rem;
          }

          @media (max-width: 1200px) {
            .table-header th,
            .table td {
              padding: 0.5rem 0.25rem;
              font-size: 0.8rem;
            }

            .category-badge,
            .status-badge,
            .payment-mode-badge {
              font-size: 0.65rem;
              padding: 0.2rem 0.4rem;
            }
          }

          @media (max-width: 768px) {
            .summary-cards-grid {
              grid-template-columns: repeat(2, 1fr);
            }

            .export-buttons {
              justify-content: center;
              margin-top: 1rem;
            }

            .table-responsive {
              font-size: 0.75rem;
            }

            .table-header th,
            .table td {
              padding: 0.4rem 0.2rem;
            }
          }

          @media (max-width: 576px) {
            .main-sec {
              padding: 1rem 0;
            }

            .summary-cards-grid {
              grid-template-columns: 1fr;
            }

            .summary-card {
              padding: 1rem;
            }

            .summary-content h4 {
              font-size: 1.25rem;
            }

            .filter-card .card-body .row {
              gap: 0.5rem;
            }

            .table-responsive {
              overflow-x: auto;
            }
          }
        `}
      </style>
    </>
  );
}
